import { useEffect, useState, useRef, useMemo } from 'react';
import Card from '../../components/card/Card';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import { getCommitteeApprovalHistory } from '../../services/ApplicationService';
import { hasData } from '../../utils/Utils';
import P from '../../components/texts/p/P';
import Image from '../../components/image/Image';
import { useConfig } from '../../context/ConfigContext';
import ApprovalHistoryTimeline from './ApprovalHistoryTimeline';
import {
  committeeCommentWrapStyle,
  isCementosLegacyCommittee,
} from '../../utils/committeeApprovalComment';

interface ApprovalHistoryProps {
  cuit: string;
  applicationId: string;
  /**
   * Task 50111 — MEMBER_QUORUM: ciclo activo según `quorum.cycleId`. Cuando se
   * pasa, el timeline destaca ese bloque como "Actual". Si no se pasa, se asume
   * que el último ciclo presente en la respuesta es el actual.
   */
  currentCycleId?: number;
}

interface ApprovalHistoryItem {
  employeeName: string;
  roleLabel: string;
  statusLabel: string;
  description: string;
  updatedAt: string;
  cycleId?: number;
}

const ApprovalHistory: React.FC<ApprovalHistoryProps> = ({
  cuit,
  applicationId,
  currentCycleId,
}) => {
  const appConfig = useConfig();
  const isCommitteeMemberQuorumEnabled =
    appConfig.isCommitteeMemberQuorumEnabled === true;
  const wrapLongComments = isCementosLegacyCommittee(appConfig);
  const [historyData, setHistoryData] = useState<ApprovalHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const lastRequest = useRef(0);

  const columns = useMemo(
    () => [
      {
        name: 'Fecha',
        selector: (row: ApprovalHistoryItem) => (
          <P type='4' variant='neutral'>
            {row.updatedAt}
          </P>
        ),
        grow: 0.2,
      },
      {
        name: 'Usuario',
        selector: (row: ApprovalHistoryItem) => (
          <P type='4' variant='neutral'>
            {row.employeeName}
          </P>
        ),
        grow: 0.8,
      },
      {
        name: 'Rol',
        selector: (row: ApprovalHistoryItem) => (
          <P type='4' variant='neutral'>
            {row.roleLabel}
          </P>
        ),
        grow: 1,
      },
      {
        name: 'Acción',
        selector: (row: ApprovalHistoryItem) => (
          <P type='4' variant='neutral'>
            {row.statusLabel}
          </P>
        ),
        grow: 0.3,
      },
      {
        name: 'Comentario',
        selector: (row: ApprovalHistoryItem) => (
          <P
            type='4'
            variant='neutral'
            style={wrapLongComments ? committeeCommentWrapStyle : undefined}
          >
            {row.description || '-'}
          </P>
        ),
        grow: 1.8,
      },
    ],
    [wrapLongComments],
  );

  const tableCustomStyles = useMemo(
    () =>
      wrapLongComments
        ? {
            rows: {
              style: {
                minHeight: '56px',
              },
            },
            cells: {
              style: {
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                height: 'auto',
                minHeight: '56px',
              },
            },
          }
        : undefined,
    [wrapLongComments],
  );

  useEffect(() => {
    if (!cuit || !applicationId) return;

    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    getCommitteeApprovalHistory(cuit, applicationId)
      .then((response: any) => {
        if (lastRequest.current !== requestId) return;

        if (hasData<ApprovalHistoryItem[] | { content: ApprovalHistoryItem[] }>(response)) {
          const data = response.data;
          setHistoryData(Array.isArray(data) ? data : data.content || []);
        } else {
          setHistoryData([]);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setHistoryData([]);
        setIsLoading(false);
      });
  }, [cuit, applicationId]);

  return (
    <Card width='100%'>
      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : historyData.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <div
            style={{ maxHeight: '60vh', overflowY: 'auto', width: '100%' }}
          >
            {isCommitteeMemberQuorumEnabled ? (
              <ApprovalHistoryTimeline
                items={historyData}
                currentCycleId={currentCycleId}
              />
            ) : (
              <Table
                columns={columns}
                data={historyData}
                tableData={{}}
                disablePagination={true}
                customStyles={tableCustomStyles}
              />
            )}
          </div>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='100%'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay historial de aprobaciones.
          </P>
        </Card>
      )}
    </Card>
  );
};

export default ApprovalHistory;
